/* eslint-disable no-param-reassign */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

const name = "wireImportBatches";

type WireImportBatchesState = {
  byId: Record<string, API.WireImport.WireImportBatch>;
  list: string[];
  pageMap: Record<string, string[]>;
  total: number;
  selected: API.WireImport.WireImportBatch | null;
};

const initialState: WireImportBatchesState = {
  byId: {},
  list: [],
  pageMap: {},
  total: 0,
  selected: null,
  // TODO: Add filters state (state_in, date range, amount range)
};

const slice = createSlice({
  name,
  initialState,
  reducers: {
    upsertPage: (
      state,
      action: PayloadAction<{
        pageNumber: number;
        results: API.WireImport.WireImportBatch[];
        total: number;
      }>,
    ) => {
      const { results, total, pageNumber } = action.payload;

      results.forEach((batch: API.WireImport.WireImportBatch) => {
        state.byId[batch.id] = batch;
      });

      state.pageMap[pageNumber] = results.map(
        (batch: API.WireImport.WireImportBatch) => batch.id,
      );
      state.total = total;
    },

    appendResults: (
      state,
      action: PayloadAction<{
        results: API.WireImport.WireImportBatch[];
        total: number;
      }>,
    ) => {
      const { results, total } = action.payload;

      const existingIds = new Set(state.list);
      results.forEach((batch: API.WireImport.WireImportBatch) => {
        state.byId[batch.id] = batch;
        if (!existingIds.has(batch.id)) {
          state.list.push(batch.id);
          existingIds.add(batch.id);
        }
      });
      state.total = total;
    },

    replaceResults: (
      state,
      action: PayloadAction<{
        results: API.WireImport.WireImportBatch[];
        total: number;
      }>,
    ) => {
      const { results, total } = action.payload;
      results.forEach((batch: API.WireImport.WireImportBatch) => {
        state.byId[batch.id] = batch;
      });
      state.list = results.map((batch) => batch.id);
      state.total = total;
    },

    updateBatchState: (
      state,
      action: PayloadAction<{
        id: string;
        state: API.WireImport.WireImportBatchState;
      }>,
    ) => {
      const batch = state.byId[action.payload.id];
      if (batch) {
        batch.state = action.payload.state;
      }
      if (state.selected && state.selected.id === action.payload.id) {
        state.selected.state = action.payload.state;
      }
    },

    updateSelected: (
      state,
      action: PayloadAction<API.WireImport.WireImportBatch | null>,
    ) => {
      state.selected = action.payload;
    },

    resetState: (state) => {
      Object.assign(state, initialState);
    },
  },
});

export const { actions } = slice;

export default {
  [name]: slice.reducer,
};
